/**
 * OrgSwitchToast — post-org-switch confirmation toast.
 *
 * Privacy audit Wave 5 polish 2026-05-22 — closes the "silent switch"
 * UX gap. Pre-fix, when a cross-org user with different roles per
 * tenant clicked OrgSwitcher → the page reloaded → the new layout
 * appeared — but with no clear signal that anything changed beyond
 * the URL. Cross-org users (1-2% of SMB customers) reported confusion
 * about which role they were "in" after switching.
 *
 * Flow:
 *   1. OrgSwitcher.handlePick writes
 *      sessionStorage[SWITCH_TOAST_KEY] = JSON.stringify({orgName, roleCode})
 *      then triggers window.location.reload()
 *   2. After reload, this component mounts inside the new app shell
 *   3. Reads the key on mount, shows a Snackbar:
 *      "Switched to {orgName} — you're now acting as {Role}"
 *   4. Removes the key from sessionStorage so the toast doesn't
 *      re-fire on subsequent navigations / reloads
 *
 * The Snackbar auto-hides after 4 seconds. Top-center anchor matches
 * the rest of the app's snackbar convention (svdy-ui §1).
 *
 * Mount this once in MainLayout / WorkerLayout — not inside TopNav
 * (TopNav is large and mounts/unmounts on layout transitions; this
 * component should survive those).
 */
import React, { useEffect, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SWITCH_TOAST_KEY, roleLabel } from './OrgSwitcher';

interface ToastPayload {
  orgName: string;
  roleCode: string | null;
}

const OrgSwitchToast: React.FC = () => {
  const { t } = useTranslation();
  const [payload, setPayload] = useState<ToastPayload | null>(null);

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(SWITCH_TOAST_KEY);
      if (raw) sessionStorage.removeItem(SWITCH_TOAST_KEY);
    } catch {
      // sessionStorage might be disabled (incognito); silently skip.
      return;
    }
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<ToastPayload>;
      if (typeof parsed?.orgName === 'string') {
        setPayload({
          orgName: parsed.orgName,
          roleCode: parsed.roleCode ?? null,
        });
      }
    } catch {
      // Malformed payload — ignore. Already removed from storage above.
    }
  }, []);

  if (!payload) return null;

  return (
    <Snackbar
      open={true}
      autoHideDuration={4000}
      onClose={() => setPayload(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        severity="success"
        variant="filled"
        onClose={() => setPayload(null)}
        sx={{ width: '100%' }}
      >
        <span dangerouslySetInnerHTML={{ __html: t('common:orgSwitcher.switchedTo', { orgName: payload.orgName }) }} />
        {payload.roleCode && (
          <span dangerouslySetInnerHTML={{ __html: t('common:orgSwitcher.actingAs', { role: roleLabel(payload.roleCode) }) }} />
        )}
      </Alert>
    </Snackbar>
  );
};

export default OrgSwitchToast;
